import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors} from "@angular/forms";

import {Store} from "@ngrx/store";
import {AppState} from "../../../state/app.state";
import {Router} from "@angular/router";

/* Services */
import {UserService} from "../../../services/user.service";
import {AuthService} from "../../../services/auth.service";
import {PopupService} from "../../../services/popup.service";
import {ApiService} from "../../../services/api.service";

import {Subscription, filter, take, Observable, skip} from "rxjs";

import { loadCountries } from "../../../state/list/list.actions";
import { selectCountriesSuccess, selectCountriesFailure } from '../../../state/list/list.selectors';

import {
  deleteBillingProfile,
  fetchBillingProfile,
  submitBillingProfile,
  updateBillingProfile
} from "../../../state/billingProfile/billingProfile.actions";
import {
  selectBillingProfileData,
  selectBillingProfileError,
  selectBillingProfileLoading,
  selectSubmitFailure,
  selectSubmitSuccess,
  selectUpdateSuccess,
  selectUpdateFailure,
  selectDeleteSuccess,
  selectDeleteError
} from "../../../state/billingProfile/billingProfile.selectors";
import {HeaderService} from "../../../services/header.service";

@Component({
  selector: 'app-billing-profile',
  templateUrl: './billing-profile.component.html',
  styleUrl: './billing-profile.component.scss'
})
export class BillingProfileComponent implements OnInit, OnDestroy {

  @Output() toggleCompanyChange = new EventEmitter<boolean>();

  user: any;

  name: string = "";
  email: string = "";

  form: FormGroup;
  errorMessages: any;
  isLoading: boolean = true;
  isCompany: boolean = true;
  countries: any[] = [];

  private successSubscription: Subscription;
  private failureSubscription: Subscription;

  private subscriptions = new Subscription();

  deleteSuccess$: Observable<boolean>;
  deleteError$: Observable<any>;

  billingProfile: any;
  hasBillingProfile: boolean = false;

  isCancelCompanyBillingVisible: boolean = false;

  addBillingCompany: boolean = false;

  // Track if we're creating a new profile to show the success message
  private isCreatingProfile: boolean = false;

  constructor(
    public formBuilder: FormBuilder,
    private popupService: PopupService,
    private store: Store<AppState>,
    public router: Router,
    private authService: AuthService,
    private headerService: HeaderService
  ) {

    this.headerService.setHeaderState('Company Billing', null, true);

    this.form = this.formBuilder.group({
      company_name: ["", this.isCompany ? [Validators.required, Validators.minLength(2), Validators.maxLength(255), this.noWhitespaceValidator] : []],
      company_vat: ["", this.isCompany ? [Validators.required, Validators.minLength(2), Validators.maxLength(50), this.noWhitespaceValidator] : []],
      company_country_id: ["", this.isCompany ? [Validators.required] : []],
      company_county: ["", this.isCompany ? [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noWhitespaceValidator] : []],
      company_town: ["", this.isCompany ? [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noWhitespaceValidator] : []],
      company_zip_code: ["", this.isCompany ? [Validators.required, Validators.minLength(2), Validators.maxLength(20), this.noWhitespaceValidator] : []],
      company_address: ["", this.isCompany ? [Validators.required, Validators.minLength(5), Validators.maxLength(255), this.noWhitespaceValidator] : []],
      company_is_vat_payer: [false],  // No validators needed, defaults to false
    });

    this.errorMessages = {
      company_name: [
        { type: "required", message: "Company name is required" },
        { type: "minlength", message: "Company name must be at least 2 characters long" },
        { type: "maxlength", message: "Company name cannot exceed 255 characters" },
        { type: "whitespace", message: "Company name cannot be only spaces" },
      ],
      company_vat: [
        { type: "required", message: "VAT is required" },
        { type: "minlength", message: "VAT must be at least 2 characters long" },
        { type: "maxlength", message: "VAT cannot exceed 50 characters" },
        { type: "whitespace", message: "VAT cannot be only spaces" },
      ],
      company_country_id: [
        { type: "required", message: "Country is required" },
      ],
      company_county: [
        { type: "required", message: "State / County is required" },
        { type: "minlength", message: "State / County must be at least 2 characters long" },
        { type: "maxlength", message: "State / County cannot exceed 100 characters" },
        { type: "whitespace", message: "State / County cannot be only spaces" },
      ],
      company_town: [
        { type: "required", message: "City is required" },
        { type: "minlength", message: "City must be at least 2 characters long" },
        { type: "maxlength", message: "City cannot exceed 100 characters" },
        { type: "whitespace", message: "City cannot be only spaces" },
      ],
      company_zip_code: [
        { type: "required", message: "Zip Code is required" },
        { type: "minlength", message: "Zip Code must be at least 2 characters long" },
        { type: "maxlength", message: "Zip Code cannot exceed 20 characters" },
        { type: "whitespace", message: "Zip Code cannot be only spaces" },
      ],
      company_address: [
        { type: "required", message: "Address is required" },
        { type: "minlength", message: "Address must be at least 5 characters long" },
        { type: "maxlength", message: "Address cannot exceed 255 characters" },
        { type: "whitespace", message: "Address cannot be only spaces" },
      ],
      country_id: [
        { type: "required", message: "Country is required" },
      ],
    };

    this.deleteSuccess$ = this.store.select(selectDeleteSuccess);
    this.deleteError$ = this.store.select(selectDeleteError);

  }

  // Custom validator to prevent whitespace-only values
  private noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.toggleCompany()
    this.getCountries()
    this.fetchBillingProfile()
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();

    if (this.successSubscription) {
      this.successSubscription.unsubscribe();
    }

    if (this.failureSubscription) {
      this.failureSubscription.unsubscribe();
    }
  }

  fetchBillingProfile () {

    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

    // Dispatch action to fetch the billing profile
    this.store.dispatch(fetchBillingProfile());

    // Subscribe to the billing profile data
    this.subscriptions.add(
      this.store.select(selectBillingProfileData).subscribe(data => {
        if (data) {
          if (data.data) {
            // Handle array response (from fetch)
            if (Array.isArray(data.data) && data.data.length > 0) {
              const wasCreating = this.isCreatingProfile;

              this.hasBillingProfile = true;
              this.handleAddCompanyBilling();
              this.billingProfile = data.data[0];
              this.updateForm(this.billingProfile);

              // Show success message if we were creating
              if (wasCreating) {
                this.popupService.notify('Your billing profile has been successfully created.', 'Billing Profile Created', 'success');
                this.form.markAsPristine();
                this.form.markAsUntouched();
                this.isCreatingProfile = false;
              }
            }
            // Handle object response (from create/update)
            else if (data.data && !Array.isArray(data.data)) {
              const wasCreating = this.isCreatingProfile;

              this.hasBillingProfile = true;
              this.handleAddCompanyBilling();
              this.billingProfile = data.data;
              this.updateForm(this.billingProfile);

              // Show success message if we were creating
              if (wasCreating) {
                this.popupService.notify('Your billing profile has been successfully created.', 'Billing Profile Created', 'success');
                this.form.markAsPristine();
                this.form.markAsUntouched();
                this.isCreatingProfile = false;
              }
            }
          }

        }
      })
    );

    // Subscribe to the loading state
    this.subscriptions.add(
      this.store.select(selectBillingProfileLoading).subscribe(loading => {
        this.isLoading = loading; // Use this to show/hide a loading spinner in your template
      })
    );

    // Handle errors in billing profile fetching
    this.subscriptions.add(
      this.store.select(selectBillingProfileError).subscribe(error => {
        if (error) {
          if (error.error && error.error.message === 'Billing profile not found !') {
            this.hasBillingProfile = false; // Use this flag to show or hide UI elements accordingly
          } else if (this.isCreatingProfile && error.error && error.error.message) {
            // Handle creation errors
            this.popupService.notify(error.error.message, 'Billing Profile Creation Failed', 'error');
            this.isCreatingProfile = false;
          }
        }
      })
    );

  }

  getCountries() {
    this.store.dispatch(loadCountries());

    // Ensure subscriptions are set up after dispatching
    this.successSubscription = this.store.select(selectCountriesSuccess).pipe(
      filter(response => response !== null), // ensure there's data
    ).subscribe({
      next: (response) => {
        this.countries = response.countries
      }
    });

    this.failureSubscription = this.store.select(selectCountriesFailure).pipe(
      filter(error => error !== null),
    ).subscribe();

  }

  toggleCompany() {
    this.isCompany = true;
    this.toggleCompanyChange.emit(this.isCompany);

    if (this.isCompany) {
      // Set validators for text fields
      this.form.controls['company_name'].setValidators([Validators.required, Validators.minLength(2), Validators.maxLength(255), this.noWhitespaceValidator]);
      this.form.controls['company_vat'].setValidators([Validators.required, Validators.minLength(2), Validators.maxLength(50), this.noWhitespaceValidator]);
      this.form.controls['company_county'].setValidators([Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noWhitespaceValidator]);
      this.form.controls['company_town'].setValidators([Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noWhitespaceValidator]);
      this.form.controls['company_zip_code'].setValidators([Validators.required, Validators.minLength(2), Validators.maxLength(20), this.noWhitespaceValidator]);
      this.form.controls['company_address'].setValidators([Validators.required, Validators.minLength(5), Validators.maxLength(255), this.noWhitespaceValidator]);

      // Country only needs required validator
      this.form.controls['company_country_id'].setValidators([Validators.required]);

      // Update validity for all controls
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].updateValueAndValidity();
      });
    } else {
      const companyControls = [
        'company_name', 'company_vat', 'company_country_id', 'company_county',
        'company_town', 'company_zip_code', 'company_address'
      ];
      companyControls.forEach(control => {
        this.form.controls[control].clearValidators();
        this.form.controls[control].reset();  // Clear validators and reset the value
      });
    }
  }

  updateForm(billingProfile: any): void {
    if (billingProfile) {

      // Handle different response structures
      // Fetch API returns nested objects (country, user), Create API returns flat structure
      const countryId = billingProfile.country?.id || billingProfile.country_id;
      const companyIsVatPayer = billingProfile.user?.vat_id ? true : (billingProfile.is_tax_payer || false);

      this.form.patchValue({
        company_name: billingProfile.company_name,
        company_vat: billingProfile.company_identifier,
        company_country_id: countryId,
        company_county: billingProfile.company_county,
        company_town: billingProfile.company_town,
        company_zip_code: billingProfile.company_zip_code,
        company_address: billingProfile.company_address,
        company_is_vat_payer: companyIsVatPayer
      });
    }

    this.isLoading = false
  }

  updateBillingProfile() {

    // Mark all controls as touched to trigger validation messages
    this.form.markAllAsTouched();

    if (this.form.valid) {

      const billingProfile = {
        user_id: this.user.id,
        company_name: this.form.value.company_name.trim(),
        company_identifier: this.form.value.company_vat.trim(),
        country_id: this.form.value.company_country_id,
        company_county: this.form.value.company_county.trim(),
        company_town: this.form.value.company_town.trim(),
        company_zip_code: this.form.value.company_zip_code.trim(),
        company_address: this.form.value.company_address.trim(),
        is_tax_payer: this.form.value.company_is_vat_payer,
      };

      if (this.hasBillingProfile) {

        const id = this.billingProfile.id;

        this.store.dispatch(updateBillingProfile({ id, billingProfile }));
        this.handleBillingProfileUpdate()

      } else {

        // Mark that we're creating a new profile
        this.isCreatingProfile = true;
        this.store.dispatch(submitBillingProfile({ billingProfile }));

      }

    }

  }

  handleBillingProfileUpdate () {

    // Handle responses
    this.subscriptions.add(
      this.store.select(selectUpdateSuccess).subscribe(profile => {
        if (profile) {
          this.popupService.notify('Your billing profile has been successfully updated.', 'Billing Profile Update Successful', 'success');
        }
      })
    );

    this.subscriptions.add(
      this.store.select(selectUpdateFailure).subscribe(error => {
        if (error?.message) {
          this.popupService.notify(error.error.message, 'Billing Profile Update Failed', 'error');
        }
      })
    );

  }

  openCancelCompanyBillingModal () {
    this.isCancelCompanyBillingVisible = true
  }

  onCancelCompanyBilling () {
    const id = this.billingProfile.id;

    this.store.dispatch(deleteBillingProfile({id}));

    // Handle delete responses
    this.subscriptions.add(
      this.store.select(selectDeleteSuccess).subscribe(success => {
        if (success) {
          this.popupService.notify('Your billing company has been successfully canceled.', 'Billing Company Canceled Successful', 'success');

          // Reset component state to show "Add Company Billing" button again
          this.hasBillingProfile = false;
          this.billingProfile = null;
          this.addBillingCompany = false;

          // Close the modal
          this.closeModal();

          // Reset the form
          this.form.reset();
          this.form.patchValue({
            company_is_vat_payer: false
          });
        }
      })
    );

    this.subscriptions.add(
      this.store.select(selectDeleteError).subscribe(error => {
        if (error?.message) {
          this.popupService.notify(error.message, 'Billing Company Canceled Failed', 'error');
        }
      })
    );

  }

  closeModal () {
    this.isCancelCompanyBillingVisible = false
  }

  handleAddCompanyBilling () {
    this.addBillingCompany = true
  }

}
